import { computed } from 'vue'
import { orderBy } from 'lodash'

import { ASSET_FIELD } from '@/entities/assets/utils/const'
import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'
import { AccountOption } from '@/entities/assets/utils/types'

import { getCurrentDate, isShortDate } from '@/helpers/dates'

import useAnalyticsStore from '@/store/analytics'
import { useAIPortfolioContext } from '@/store/ai/utils/context'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepositoriesStore } from '@/store/repositories'

import { PRIVATE_SECURITY_TYPE_EXCLUDES } from '@/store/ai/utils/const'

import { WizardDefaultSource, WizardOptionsSource } from './types'

/**
 * Resolves a field definition's options source against the repository.
 *
 * The definitions are static — a wizard folder cannot import a store and stay a
 * description of a wizard — so they name a source and this turns it into
 * values. Account types come from the same place the AI prompts quote them
 * from, so a select can never offer a type the assistant was told does not
 * exist.
 */
export const useWizardOptions = () => {
  const analyticsStore = useAnalyticsStore()
  const assetsBunchStore = useAssetsBunchStore()
  const repositoriesStore = useRepositoriesStore()

  const { accountTypesFor, assetTypesFor } = useAIPortfolioContext()

  const currencies = computed(() =>
    repositoriesStore.getCurrencies.map(currency => currency.ticker),
  )

  /** What an untouched currency field is filled with. */
  const defaultCurrency = computed(() => repositoriesStore.getCurrentCurrency)

  /*
   * The as-of date analytics is showing, from the store that owns it. Reading
   * its session key directly would have duplicated where the date lives and
   * how it is shaped, so a change to either would break this silently.
   *
   * Safe to instantiate from the app shell: the store's own refresh watcher
   * fires immediately, but it resolves to the views registered as active, and a
   * wizard opened over some other screen registers none — so `refreshAnalytics`
   * exits before it requests anything.
   *
   * Undefined until an analytics view has loaded in this repository — see
   * `defaultFor`, which falls back to today rather than to an empty field.
   */
  const analyticsEndDate = computed(() => analyticsStore.getDate)

  const optionsFor = (source?: WizardOptionsSource): string[] => {
    switch (source) {
      case 'currencies':
        return currencies.value
      case 'positionAccountTypes':
        return accountTypesFor(TRANSACTION_TYPE.ADD_POSITION)
      case 'liabilityAccountTypes':
        return accountTypesFor(TRANSACTION_TYPE.ADD_LIABILITY)
      case 'assetTypes':
        return assetTypesFor(TRANSACTION_TYPE.SUBSCRIBE_AND_FUND)
          .filter(type => !PRIVATE_SECURITY_TYPE_EXCLUDES.includes(type))
      default:
        return []
    }
  }

  /**
   * The accounts a record of this transaction type may hang off: the
   * repository's own assets, narrowed to those types.
   *
   * Feed-maintained accounts are left out, via the store's own unlinked view —
   * the connection owns those, so a record hung off one by hand is a record the
   * next sync disagrees with. That is the exclusion the picker's note promises.
   */
  const accountsOf = (transactionType: string): AccountOption[] => {
    const types = accountTypesFor(transactionType)
    if (!types.length) return []

    const accounts: AccountOption[] = []
    // getUnlinkedList is the store's own "not feed-maintained" view; filtering
    // on isLinked here would be a second copy of that rule.
    for (const asset of assetsBunchStore.getUnlinkedList.values()) {
      const type = asset.field<string>(ASSET_FIELD.TYPE).value
      if (!type || !types.includes(type)) continue

      accounts.push({
        id: asset.id,
        name: asset.field<string>(ASSET_FIELD.NAME).value,
        ticker: asset.field<string>(ASSET_FIELD.TICKER).value,
        type,
      })
    }

    return orderBy(accounts, account => account.name?.toLowerCase())
  }

  /**
   * Every account the repository has, as ids alone.
   *
   * Broader than `accountsOf` on purpose: that answers "what may this step
   * offer", and this answers "does this account exist" — which is the only
   * question an id from the assistant raises. Feed-maintained accounts are
   * still out, for the reason `accountsOf` gives.
   */
  const accountIds = (): string[] =>
    Array.from(assetsBunchStore.getUnlinkedList.values()).map(asset => asset.id)

  /*
   * What an untouched defaulted field resolves to. The same value the
   * placeholder names, read from the same place — so what the wizard promises
   * and what it creates cannot drift apart.
   */
  const defaultFor = (source: WizardDefaultSource): string => {
    switch (source) {
      case 'currency':
        return defaultCurrency.value
      /*
       * Today when analytics has no as-of date to inherit — which is most of
       * the time, since the date is only set once an analytics view has
       * actually loaded, and Add asset opens from the app shell. Leaving the
       * field empty put the burden of typing a date on every user who had not
       * been to Analytics first.
       *
       * The stored date is validated rather than trusted: the field is an ISO
       * date and the schema enforces it, so a value in any other shape falls
       * back to today rather than reaching a control that cannot parse it.
       */
      case 'analyticsEndDate':
        return isShortDate(analyticsEndDate.value ?? '')
          ? analyticsEndDate.value!
          : getCurrentDate({ iso: true })
      default:
        return ''
    }
  }

  /*
   * `defaultCurrency` is deliberately not exposed: the one thing anyone wanted
   * it for was naming the default in a placeholder, and a defaulted field now
   * holds the value itself. `defaultFor` is how the wizard asks.
   */
  return { optionsFor, accountTypesFor, accountsOf, accountIds, defaultFor }
}
